import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, Send, Loader2, CheckCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_SHARE_TEXT = encodeURIComponent("I just completed the Burnout To Balance In Action course by @LemonadeLivingCo and it was transformative! If you're struggling with burnout, I highly recommend it. #BurnoutToBalance #MentalWellness #LemonadeLiving");
const SITE_URL = encodeURIComponent("https://www.thelemonadelivingcompany.com/course");

const ReviewForm = ({ email, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { toast.error("Please select a rating."); return; }
    if (!reviewText.trim()) { toast.error("Please write your review."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reviews/submit`, { email, rating, review_text: reviewText, name, role });
      if (res.data.success) {
        setSubmitted(true);
        if (onSubmitted) onSubmitted();
      } else {
        toast.info(res.data.message);
      }
    } catch (err) { toast.error("Failed to submit review."); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div data-testid="review-submitted" className="rounded-2xl p-8 text-center space-y-4" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--ll-green)" }}>
          <CheckCircle size={28} style={{ color: "white" }} />
        </div>
        <h3 className="font-serif text-xl font-medium" style={{ color: "var(--ll-text)" }}>Thank You For Your Review!</h3>
        <p className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
          Your review will appear on our site after approval. In the meantime, would you share your experience on social media?
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${SITE_URL}`} target="_blank" rel="noopener noreferrer"
            data-testid="share-linkedin"
            className="rounded-full px-5 py-2.5 text-xs font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
            style={{ background: "#0A66C2", color: "white", fontFamily: "'Outfit', sans-serif" }}>
            <Share2 size={12} /> Share on LinkedIn
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${SOCIAL_SHARE_TEXT}&url=${SITE_URL}`} target="_blank" rel="noopener noreferrer"
            data-testid="share-twitter"
            className="rounded-full px-5 py-2.5 text-xs font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
            style={{ background: "#1DA1F2", color: "white", fontFamily: "'Outfit', sans-serif" }}>
            <Share2 size={12} /> Share on Twitter/X
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${SITE_URL}`} target="_blank" rel="noopener noreferrer"
            data-testid="share-facebook"
            className="rounded-full px-5 py-2.5 text-xs font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
            style={{ background: "#1877F2", color: "white", fontFamily: "'Outfit', sans-serif" }}>
            <Share2 size={12} /> Share on Facebook
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="review-form" className="rounded-2xl p-6 space-y-5" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <div className="flex items-center gap-3">
        <Star size={20} style={{ color: "var(--ll-accent)" }} />
        <div>
          <h3 className="font-serif text-lg font-medium" style={{ color: "var(--ll-text)" }}>Leave a Review</h3>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Your honest feedback helps others and helps us improve.</p>
        </div>
      </div>

      {/* Star Rating */}
      <div>
        <Label className="text-sm mb-2 block" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Rating *</Label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={`rating-${n}`} type="button" data-testid={`rating-star-${n}`}
              onMouseEnter={() => setHoverRating(n)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(n)}
              className="p-1 transition-transform hover:scale-110">
              <Star size={28} fill={n <= (hoverRating || rating) ? "var(--ll-accent)" : "none"}
                style={{ color: n <= (hoverRating || rating) ? "var(--ll-accent)" : "var(--ll-border)" }} />
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Display Name</Label>
          <Input data-testid="review-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="How you'd like to appear" className="rounded-xl border bg-white/50" style={{ borderColor: "var(--ll-border)" }} />
        </div>
        <div className="space-y-2">
          <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Role / Title</Label>
          <Input data-testid="review-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g., Software Engineer" className="rounded-xl border bg-white/50" style={{ borderColor: "var(--ll-border)" }} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Your Review *</Label>
        <Textarea data-testid="review-text" value={reviewText} onChange={(e) => setReviewText(e.target.value)} required
          placeholder="What did you find most valuable? How has the course helped you? Would you recommend it?" rows={4}
          className="rounded-xl border bg-white/50 resize-none" style={{ borderColor: "var(--ll-border)" }} />
      </div>

      <button type="submit" data-testid="review-submit" disabled={loading}
        className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {loading ? "Submitting..." : "Submit Review"}
      </button>
    </form>
  );
};

export default ReviewForm;
